/**
 * Student auth page — combined Sign Up + Sign In flow.
 *
 * Mounted at /aluno-login. Toggles between "register" and "login" mode via
 * a small tab. On success, persists the JWT to localStorage (via the
 * studentAuth lib) and bounces the user to wherever they came from
 * (`returnTo` query param, falls back to `/`).
 *
 * Guest mode is preserved — there is a prominent "Continuar como convidado"
 * link that bypasses login entirely.
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { LogIn, UserPlus, ArrowLeft } from "lucide-react";
import SiteChrome from "@/components/SiteChrome";
import { login as doLogin, register as doRegister, getCurrentUser } from "@/lib/studentAuth";
import { NATIONALITIES } from "@/lib/nationalities";

const NATIONS = NATIONALITIES;

function formatErr(detail) {
  if (!detail) return "Algo correu mal. Tenta novamente.";
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === "string" ? e.msg : "")).filter(Boolean).join(" ");
  return String(detail);
}

export default function AlunoLogin() {
  const [mode, setMode] = useState("login"); // "login" | "register"
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [school, setSchool] = useState("");
  const [nationality, setNationality] = useState("PT");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nav = useNavigate();
  const [params] = useSearchParams();
  const returnTo = params.get("returnTo") || "/";

  // Already logged in? Skip the page.
  useEffect(() => {
    if (getCurrentUser()) nav(returnTo, { replace: true });
  }, [nav, returnTo]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!email.trim() || !password) {
      setError("Email e password são obrigatórios.");
      return;
    }
    if (mode === "register" && !displayName.trim()) {
      setError("Indica um nome a apresentar.");
      return;
    }
    setBusy(true);
    try {
      if (mode === "register") {
        await doRegister({ email: email.trim(), password, display_name: displayName.trim(), school: school.trim(), nationality });
        toast.success("Conta criada! Bem-vindo(a) ao SuperTmatik.");
      } else {
        await doLogin({ email: email.trim(), password });
        toast.success("Sessão iniciada.");
      }
      nav(returnTo, { replace: true });
    } catch (err) {
      setError(formatErr(err?.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <SiteChrome>
      <main className="flex-1 px-4 py-10 flex items-start justify-center">
        <div className="w-full max-w-md bg-white rounded-3xl shadow-xl p-6 md:p-8" data-testid="aluno-login-card">
          <div className="flex gap-2 mb-6 p-1 bg-slate-100 rounded-full">
            <button
              data-testid="tab-login"
              type="button"
              onClick={() => { setMode("login"); setError(""); }}
              className={`flex-1 py-2 rounded-full text-sm font-bold transition ${mode === "login" ? "bg-amber-400 text-amber-950 shadow" : "text-slate-600"}`}
            >
              <LogIn className="inline w-4 h-4 mr-1" /> Entrar
            </button>
            <button
              data-testid="tab-register"
              type="button"
              onClick={() => { setMode("register"); setError(""); }}
              className={`flex-1 py-2 rounded-full text-sm font-bold transition ${mode === "register" ? "bg-amber-400 text-amber-950 shadow" : "text-slate-600"}`}
            >
              <UserPlus className="inline w-4 h-4 mr-1" /> Criar conta
            </button>
          </div>

          <form onSubmit={submit} className="space-y-3" data-testid="aluno-auth-form">
            {mode === "register" && (
              <>
                <div>
                  <Label htmlFor="name">Nome a apresentar</Label>
                  <Input id="name" data-testid="input-display-name" value={displayName} onChange={(e) => setDisplayName(e.target.value)} placeholder="Ex: Ana" autoComplete="name" />
                </div>
                <div>
                  <Label htmlFor="school">Escola (opcional)</Label>
                  <Input id="school" data-testid="input-school" value={school} onChange={(e) => setSchool(e.target.value)} placeholder="Escola Básica do Loureiro" autoComplete="organization" />
                </div>
                <div>
                  <Label htmlFor="nat">Nacionalidade</Label>
                  <select
                    id="nat"
                    data-testid="select-nationality"
                    value={nationality}
                    onChange={(e) => setNationality(e.target.value)}
                    className="w-full rounded-md border border-slate-300 px-3 py-2 text-sm bg-white"
                  >
                    {NATIONS.map((n) => (<option key={n.code} value={n.code}>{n.label}</option>))}
                  </select>
                </div>
              </>
            )}
            <div>
              <Label htmlFor="email">Email</Label>
              <Input id="email" type="email" data-testid="input-email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="ana@exemplo.com" autoComplete="email" required />
            </div>
            <div>
              <Label htmlFor="password">Password</Label>
              <Input id="password" type="password" data-testid="input-password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder={mode === "register" ? "Mínimo 6 caracteres" : "********"} autoComplete={mode === "register" ? "new-password" : "current-password"} required />
            </div>

            {error && <div data-testid="auth-error" className="text-sm font-bold text-rose-600 bg-rose-50 border border-rose-200 rounded-md px-3 py-2">{error}</div>}

            <Button
              type="submit"
              data-testid="btn-submit"
              disabled={busy}
              className="w-full bg-amber-400 hover:bg-amber-500 text-amber-950 font-[Fredoka] font-bold text-base py-5 rounded-full border-b-4 border-amber-600 active:border-b-0 active:translate-y-[2px] disabled:opacity-50"
            >
              {busy ? "A processar…" : (mode === "register" ? "Criar conta" : "Entrar")}
            </Button>
          </form>

          <div className="mt-5 text-center">
            <Link to={returnTo} data-testid="btn-continue-guest" className="text-sm text-slate-600 hover:text-slate-900 underline">
              <ArrowLeft className="inline w-3 h-3 mr-1" /> Continuar como convidado
            </Link>
          </div>
        </div>
      </main>
    </SiteChrome>
  );
}
